// src/store/account/listener.ts
import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { getPaymentDetails } from '@/account/fetch/get-payment-details'
import { getOrganizationDirectoryUsers } from '@/organizations/fetch/get-organization-directory-users'
import { AccountActions } from './slice'
import { kPaymentDetailsCacheDuration } from '../constants'

export const accountListenerMiddleware = createListenerMiddleware()
const startListener = accountListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * Refresh Payment Details Listener
 *
 * Gets the payment details for the current user and populates the account state.
 */
startListener({
  actionCreator: AccountActions.refreshPaymentDetails,
  effect: async (action, listenerApi) => {
    const { forceRefresh } = action.payload

    // Get the existing state
    const state = listenerApi.getState().accountState

    // Is loading already?
    const isLoading = state.loading

    // Cache Handling
    const cacheDuration = kPaymentDetailsCacheDuration
    const lastFetch = state.lastRefresh
    const cacheExpired = lastFetch === null || Date.now() - lastFetch > cacheDuration

    // Return early if:
    // - the cache has not expired and we are not forcing a refresh
    // - the payment details are already loading
    if ((!cacheExpired && !forceRefresh) || isLoading) {
      return
    }

    listenerApi.dispatch(AccountActions.setLoading(true))
    listenerApi.dispatch(AccountActions.setError(false))

    try {
      const response = await getPaymentDetails()

      const customerPaymentDetails = response.customer_payment_details

      if (customerPaymentDetails) {
        // Populate details into state

        // Subscription details
        listenerApi.dispatch(AccountActions.setStripeSubscriptionStatus(customerPaymentDetails?.subscription_status ?? null))
        listenerApi.dispatch(AccountActions.setTrialEnd(customerPaymentDetails?.trial_end ?? null))
        listenerApi.dispatch(AccountActions.setCancelAtPeriodEnd(customerPaymentDetails?.cancel_at_period_end ?? null))
        listenerApi.dispatch(AccountActions.setStripeSubscriptionInterval(customerPaymentDetails?.interval ?? null))
        listenerApi.dispatch(AccountActions.setHasLegacyStudentPlan(customerPaymentDetails?.has_legacy_student_plan ?? false))

        // Billing details
        listenerApi.dispatch(AccountActions.setUpcomingInvoiceAmountDueInCents(customerPaymentDetails?.upcoming_invoice_summary?.amount_due ?? null))

        listenerApi.dispatch(
          AccountActions.setDiscountInfo({
            hasDiscount: customerPaymentDetails?.coupon !== null, // If there is a coupon, there is a discount
            appliedCoupon: customerPaymentDetails?.coupon ?? null,
          })
        )

        listenerApi.dispatch(
          AccountActions.setSeatQuantityAndRate({
            numberOfSeats: customerPaymentDetails?.quantity ?? null,
            costPerSeatInCents: customerPaymentDetails?.unit_amount_cents ?? null,
          })
        )

        // Set initial load complete
        listenerApi.dispatch(AccountActions.setInitialLoadComplete(true))
      }
    } catch (e) {
      console.error('AccountListener: Error fetching payment details', e)
      listenerApi.dispatch(AccountActions.setError(true))
    } finally {
      listenerApi.dispatch(AccountActions.setLoading(false))
      listenerApi.dispatch(AccountActions.setLastRefresh(Date.now()))
    }
  },
})

/**
 * Refresh Org Directory Users Listener
 *
 * Fetches the list of org members visible to all org members and stores them in account state.
 * Used to gate collaboration features without relying on the seats column.
 */
startListener({
  actionCreator: AccountActions.refreshOrgDirectoryUsers,
  effect: async (_action, listenerApi) => {
    try {
      const response = await getOrganizationDirectoryUsers()
      listenerApi.dispatch(AccountActions.setOrgDirectoryUsers(response.users))
    } catch (e) {
      console.error('AccountListener: Error fetching org directory users', e)
    }
  },
})
